// The job's cut list.
// Answers: what parts does the shop cut for this job, from which material, and how big?
// It is read from the job as the engine priced it; nothing here is typed. A locked job shows
// the parts of the job as it stands (the same items the lock froze).

import { cutList, cutListCsv, formatLength } from '@zerogap/engine';
import { qty } from '../../lib/format';
import { Btn, Empty, Info, Panel, pressable } from '../../ui/kit';
import type { JobCtx } from './Workspace';

const GRAIN: Record<string, string> = { length: 'along the length', width: 'along the width', none: 'no grain' };
// banded edges are named by the part's own sides: along its length, along its width (Spencer, 2026-10-14)
const edges = (long: string, short: string) => {
  const l = Number(long);
  const s = Number(short);
  if (l + s === 0) return '—';
  return [l > 0 ? `${qty(l)} length ${l === 1 ? 'edge' : 'edges'}` : '', s > 0 ? `${qty(s)} width ${s === 1 ? 'edge' : 'edges'}` : ''].filter(Boolean).join(' + ');
};

export function CutList({ ctx }: { ctx: JobCtx }) {
  const res = ctx.r.res!;
  const list = cutList(res.items);
  const title = `${ctx.data.opportunity?.name ?? 'job'} ${ctx.data.job?.scope_name ?? ''}`.trim();

  const download = () => {
    const blob = new Blob([cutListCsv(list)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/[^A-Za-z0-9]+/g, '-')}-cut-list.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (list.materials.length === 0) {
    return (
      <Panel label="Cut list">
        <Empty>
          No item on this job lists its parts yet. A cut list comes from the catalog: a shop-made item lists the parts it is cut from (Library → Catalog → the item → Parts), and every job that uses it gets its cut list worked out as the items are
          entered.
        </Empty>
      </Panel>
    );
  }

  return (
    <div className="stack">
      <Panel
        label={`Cut list · ${qty(list.pieces)} pieces · ${qty(list.band_inches)} in of banded edge`}
        right={
          <span className="row mid" style={{ gap: 6 }}>
            <Info left>Every part of every shop-made item, worked out from the item's fields and the job's standards each time the job is priced. Sizes are finished sizes. Length runs with the grain.</Info>
            <Btn small read onClick={download}>
              Download as CSV
            </Btn>
          </span>
        }
      >
        <small>Change a size by changing the item, never here. When the job locks, this list is kept with the revision.</small>
      </Panel>
      {list.materials.map((m) => (
        <Panel key={m.material_id ?? m.material} label={`${m.material} · ${qty(m.pieces)} pieces · ${m.area_sq_ft} sq ft`} soft={!m.material_id}>
          {!m.material_id && <div className="warn">This material is not chosen on the job yet (Job setup).</div>}
          <div className="tblwrap">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Part</th>
                  <th className="right">Qty</th>
                  <th className="right">Length</th>
                  <th className="right">Width</th>
                  <th>Grain</th>
                  <th>Banded edges</th>
                  <th>Banding</th>
                </tr>
              </thead>
              <tbody>
                {m.rows.map((r, i) => (
                  <tr key={i} className="click" {...pressable(() => ctx.select([r.item_id]), 'row')}>
                    <td>
                      <b>{r.item_number}</b>
                    </td>
                    <td>
                      <b>{r.label}</b> <small>· {r.part}</small>
                      {r.note && <small> · {r.note}</small>}
                    </td>
                    <td className="right num">{qty(r.qty)}</td>
                    <td className="right num">{formatLength(r.length)}</td>
                    <td className="right num">{formatLength(r.width)}</td>
                    <td>
                      {GRAIN[r.grain]}
                      {r.grain_locked && <small> · locked</small>}
                    </td>
                    <td>{edges(r.band_long, r.band_short)}</td>
                    <td>{r.banding === null ? '—' : r.banding.startsWith('(') ? <span className="warn">{r.banding}</span> : r.banding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
    </div>
  );
}
